import { useMemo } from "react";
import { fetchData } from "../api/client";
import { mergeWhatsappFilterOptions } from "../utils/whatsappCampaignFilterOptions";
import { useCachedFilterOptions } from "./useCachedFilterOptions";

/** Extrai array de listas da API (aceita data aninhada / items / agents / users). */
export function extractFilterListPayload(res) {
  const raw = res?.data ?? res;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.agents)) return raw.agents;
  if (Array.isArray(raw?.users)) return raw.users;
  if (Array.isArray(raw?.campaigns)) return raw.campaigns;
  if (Array.isArray(res?.agents)) return res.agents;
  return [];
}

function extractTypesTree(typesRes) {
  const raw = typesRes?.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.data?.data)) return raw.data.data;
  return [];
}

function buildWhatsappCampaignOptions(broadcastList, waList) {
  const fromApi = mergeWhatsappFilterOptions({
    broadcasts: waList.filter((c) => c?.filterTarget === "campaign"),
    legacyWhatsapp: waList.filter(
      (c) => !c?.filterTarget || c.filterTarget === "whatsapp_campaign"
    ),
  });
  if (fromApi.length) return fromApi;
  return mergeWhatsappFilterOptions({
    broadcasts: broadcastList,
    legacyWhatsapp: waList,
  });
}

function assertSomeFulfilled(results, label) {
  const rejected = results.filter((r) => r.status === "rejected");
  if (rejected.length) {
    console.error(
      `Erro parcial ao carregar ${label}:`,
      rejected.map((r) => r.reason)
    );
  }
  // Evita cachear lista vazia por 10min se a API inteira falhou (ex.: 503).
  if (results.length > 0 && rejected.length === results.length) {
    throw rejected[0].reason || new Error(`Falha ao carregar ${label}`);
  }
}

async function fetchCoreOptions() {
  const results = await Promise.allSettled([
    fetchData("/types?tree=1&limit=500", "GET"),
    fetchData("/channels?limit=200", "GET"),
    fetchData("/client-tags?limit=200&includeUsed=1", "GET"),
  ]);
  assertSomeFulfilled(results, "opções da busca avançada");
  const [typesRes, channelsRes, tagsRes] = results.map((r) =>
    r.status === "fulfilled" ? r.value : null
  );
  return {
    typesTree: extractTypesTree(typesRes),
    channels: extractFilterListPayload(channelsRes),
    clientTags: extractFilterListPayload(tagsRes),
  };
}

async function fetchDashboardExtras() {
  const results = await Promise.allSettled([
    fetchData("/products?page=1&limit=100", "GET"),
    fetchData("/campaigns/broadcasts", "GET"),
    fetchData("/campaigns/whatsapp", "GET"),
    fetchData("/dashboard/vendas/lost-reasons", "GET"),
  ]);
  assertSomeFulfilled(results, "opções do dashboard (filtros)");
  const [productsRes, broadcastsRes, waRes, motivesRes] = results.map((r) =>
    r.status === "fulfilled" ? r.value : null
  );
  const broadcastList = extractFilterListPayload(broadcastsRes);
  const waList = extractFilterListPayload(waRes);
  return {
    products: extractFilterListPayload(productsRes),
    broadcastCampaigns: broadcastList,
    whatsappCampaigns: buildWhatsappCampaignOptions(broadcastList, waList),
    lostReasons: extractFilterListPayload(motivesRes),
  };
}

const EMPTY_CORE = {
  typesTree: [],
  channels: [],
  clientTags: [],
};

const EMPTY_DASHBOARD = {
  products: [],
  broadcastCampaigns: [],
  whatsappCampaigns: [],
  lostReasons: [],
};

/**
 * Opções do AdvancedSearchModal com cache por workspace.
 * Reabrir o modal (mesmo após fechar rápido) reusa o cache sem spinner.
 */
export function useAdvancedSearchFilterOptions({ open = false, includeDashboardExtras = false } = {}) {
  const coreQuery = useCachedFilterOptions("advanced-search-core", fetchCoreOptions, {
    enabled: open,
  });
  const dashQuery = useCachedFilterOptions(
    "advanced-search-dashboard",
    fetchDashboardExtras,
    { enabled: open && includeDashboardExtras }
  );

  const core = coreQuery.data || EMPTY_CORE;
  const dash = dashQuery.data || EMPTY_DASHBOARD;

  const isLoading = Boolean(
    open &&
      (coreQuery.isLoading || (includeDashboardExtras && dashQuery.isLoading))
  );

  return useMemo(
    () => ({
      typesTree: core.typesTree,
      channels: core.channels,
      clientTags: core.clientTags,
      products: includeDashboardExtras ? dash.products : EMPTY_DASHBOARD.products,
      broadcastCampaigns: includeDashboardExtras
        ? dash.broadcastCampaigns
        : EMPTY_DASHBOARD.broadcastCampaigns,
      whatsappCampaigns: includeDashboardExtras
        ? dash.whatsappCampaigns
        : EMPTY_DASHBOARD.whatsappCampaigns,
      lostReasons: includeDashboardExtras ? dash.lostReasons : EMPTY_DASHBOARD.lostReasons,
      isLoading,
      isFetching: Boolean(coreQuery.isFetching || (includeDashboardExtras && dashQuery.isFetching)),
    }),
    [core, dash, includeDashboardExtras, isLoading, coreQuery.isFetching, dashQuery.isFetching]
  );
}
